import { Box as MuiBox, css, ListItemText, Menu, MenuItem, styled } from "@mui/material";
import { Box, colors, Search } from "@procore/core-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { ListNode } from "components/objectTree/types";
import { collapseAllNodes } from "components/objectTree/utils";
import { TOOLBAR_COLORS } from "components/toolbars/constants";
import { ObjectTreeControlsBar } from "components/window/core/objectTree/components/ObjectTreeControlsBar";
import { ObjectTreeNodeProcore } from "components/window/core/objectTree/components/ObjectTreeNodeProcore";
import { ITEM_HEIGHT } from "components/window/core/objectTree/constants";
import { BulkAction, createBulkActions } from "components/window/core/objectTree/utils/bulkActions";
import { shouldSuppressDuplicateContextMenuOpen } from "components/window/core/objectTree/utils/suppressDuplicateContextMenuOpen";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { useDispatchHidden, useHidden } from "contexts/hidden";
import { highlightActions } from "contexts/highlighted";
import { useDispatchHighlighted, useHighlighted } from "contexts/highlighted";
import { useDispatchSelectionBasket } from "contexts/selectionBasket";
import { SidePanelProps } from "contexts/sidePanels";
import { useFlyToSelected } from "features/flyToSelected/useFlyToSelected";
import { ModelTree } from "features/modelTree/components/modelTree";
import { emptyFileTypeFilters } from "features/modelTree/constants";
import { ModelTreeNodeData } from "features/modelTree/types";
import { selectDefaultVisibility, selectMainObject, selectPreIsolationVisibility } from "features/render";
import { useAbortController } from "hooks/useAbortController";
import { useCloseOnOutsidePointerDown } from "hooks/useCloseOnOutsidePointerDown";
import { useObjectTreeSearch } from "hooks/useObjectTreeSearch";
import { selectIsOnline } from "slices/explorer";
import { telemetry } from "telemetry";
import { selectObjectHierarchy, selectObjectHierarchyFromFilteredTree } from "utils/objectActions";
import { getGroupStateForFilteredTree, getGroupStateForIdSet } from "utils/treeHelpers";

const PanelContent = styled(Box)(
    () => css`
        height: 100%;
        display: flex;
        flex-direction: column;
        overflow: hidden;
    `,
);

const ControlsSection = styled(Box)(
    () => css`
        padding: 12px 16px;
        border-bottom: 1px solid ${TOOLBAR_COLORS.BORDER};
        flex-shrink: 0;
    `,
);

const TreeContainer = styled(Box)(
    () => css`
        flex: 1;
        min-height: 0;
        overflow: hidden;

        .object-tree-scroll-container {
            overflow: auto;
            overscroll-behavior-x: contain;
            touch-action: pan-x pan-y;
            scrollbar-gutter: stable;
            scrollbar-color: transparent transparent;

            &:hover {
                scrollbar-color: ${colors.gray70} transparent;
            }
        }
    `,
);

/**
 * Objects panel - integrated ObjectTreeWindow content
 */
export function ObjectsPanel(_props: SidePanelProps) {
    const { t } = useTranslation();
    const {
        state: { db },
    } = useExplorerGlobals();
    const dispatch = useAppDispatch();
    const mainObject = useAppSelector(selectMainObject);
    const defaultVisibility = useAppSelector(selectDefaultVisibility);
    const preIsolationVisibility = useAppSelector(selectPreIsolationVisibility);
    const isOnline = useAppSelector(selectIsOnline);
    const highlightedSet = useHighlighted();
    const dispatchHighlighted = useDispatchHighlighted();
    const dispatchHidden = useDispatchHidden();
    const hidden = useHidden();
    const dispatchSelectionBasket = useDispatchSelectionBasket();
    const { flyToSelected } = useFlyToSelected();

    const {
        searchTerm,
        filteredObjectIds,
        tree,
        setTree,
        treeKey,
        isSearching,
        hasNoResults,
        searchError,
        isFilterActive,
        handleSearchChange,
        handleSearchSubmit,
    } = useObjectTreeSearch(db);

    const handleCollapseAll = useCallback(() => {
        setTree((currentTree) => collapseAllNodes(currentTree));
    }, [setTree]);

    const [selectionAbortController] = useAbortController();
    const lastContextMenuOpenRef = useRef<{ nodeId: number; at: number } | null>(null);
    const contextMenuPaperRef = useRef<HTMLElement | null>(null);

    const [contextMenu, setContextMenu] = useState<{
        mouseX: number;
        mouseY: number;
        actions: BulkAction[];
        nodeId: number;
    } | null>(null);

    const closeContextMenu = useCallback(() => {
        lastContextMenuOpenRef.current = null;
        setContextMenu(null);
    }, []);

    useCloseOnOutsidePointerDown(contextMenu !== null, contextMenuPaperRef, closeContextMenu);

    const onNodeRightClick = useCallback(
        (x: number, y: number, item: ListNode<ModelTreeNodeData>) => {
            const nodeId = item.node.data.object.id;
            if (shouldSuppressDuplicateContextMenuOpen(lastContextMenuOpenRef, nodeId)) {
                return;
            }

            const selected =
                filteredObjectIds != null
                    ? getGroupStateForFilteredTree(highlightedSet.ids, item, filteredObjectIds, tree)
                    : getGroupStateForIdSet(highlightedSet.ids, item, tree);

            const isAlreadySelected = selected !== "none";

            if (!isAlreadySelected && db) {
                dispatchHighlighted(highlightActions.setIds([]));

                const objects = item.nodeGroup?.map((n) => n.data.object) ?? [item.node.data.object];

                const selectionPromise =
                    filteredObjectIds != null
                        ? selectObjectHierarchyFromFilteredTree({
                              nodes: objects,
                              filteredObjectIds,
                              dispatchHighlighted,
                              tree,
                          })
                        : selectObjectHierarchy({
                              nodes: objects,
                              db,
                              abortSignal: selectionAbortController.current.signal,
                              setLoading: () => {},
                              dispatchHighlighted,
                          });

                selectionPromise.catch((error) => {
                    if (!selectionAbortController.current.signal.aborted) {
                        console.error("Context menu selection failed:", error);
                        telemetry.trackCaughtException({
                            source: "ObjectsPanel.contextMenuSelection",
                            category: "data",
                            error,
                            severity: "error",
                        });
                    }
                });
            }

            const actions = createBulkActions(highlightedSet.idArr.length, highlightedSet.idArr, {
                t,
                dispatch,
                dispatchHighlighted,
                dispatchHidden,
                dispatchSelectionBasket,
                defaultVisibility,
                preIsolationVisibility,
                flyToSelected,
                currentMainObject: mainObject,
                onActionComplete: closeContextMenu,
                hiddenIds: hidden.ids,
                lastHiddenIds: hidden.lastHiddenIds,
            });
            setContextMenu({ mouseX: x, mouseY: y, actions, nodeId });
        },
        [
            db,
            filteredObjectIds,
            tree,
            highlightedSet.ids,
            highlightedSet.idArr,
            dispatchHighlighted,
            dispatchHidden,
            dispatchSelectionBasket,
            defaultVisibility,
            preIsolationVisibility,
            flyToSelected,
            mainObject,
            closeContextMenu,
            t,
            dispatch,
            selectionAbortController,
            hidden.ids,
            hidden.lastHiddenIds,
        ],
    );

    useEffect(() => {
        setContextMenu((openMenu) => {
            if (!openMenu) {
                return null;
            }

            const hasSelection = highlightedSet.idArr.length > 0;
            if (!hasSelection) {
                return openMenu;
            }

            const updatedActions = createBulkActions(highlightedSet.idArr.length, highlightedSet.idArr, {
                t,
                dispatch,
                dispatchHighlighted,
                dispatchHidden,
                dispatchSelectionBasket,
                defaultVisibility,
                preIsolationVisibility,
                flyToSelected,
                currentMainObject: mainObject,
                onActionComplete: closeContextMenu,
                hiddenIds: hidden.ids,
                lastHiddenIds: hidden.lastHiddenIds,
            });

            return { ...openMenu, actions: updatedActions };
        });
    }, [
        highlightedSet.idArr,
        t,
        dispatch,
        dispatchHighlighted,
        dispatchHidden,
        dispatchSelectionBasket,
        defaultVisibility,
        preIsolationVisibility,
        flyToSelected,
        mainObject,
        closeContextMenu,
        hidden.ids,
        hidden.lastHiddenIds,
    ]);

    const controlsSection = (
        <ControlsSection>
            <Search
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder={t("filterByKeyword")}
                autoComplete="off"
                onSubmit={() => void handleSearchSubmit()}
            />
        </ControlsSection>
    );

    if (!isOnline) {
        return (
            <PanelContent>
                {controlsSection}
                <MuiBox
                    sx={{
                        padding: 3,
                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        color: "text.secondary",
                    }}
                    data-testid="objects-panel-offline"
                >
                    <MuiBox sx={{ color: "text.primary", fontWeight: 600 }}>{t("noInternetConnection")}</MuiBox>
                    <MuiBox sx={{ fontSize: "0.875rem" }}>{t("objectTreeOffline")}</MuiBox>
                </MuiBox>
            </PanelContent>
        );
    }

    return (
        <PanelContent>
            {controlsSection}

            <ObjectTreeControlsBar
                tree={tree}
                filteredObjectIds={filteredObjectIds}
                hasNoResults={hasNoResults}
                onCollapseAll={handleCollapseAll}
                borderBottom={`1px solid ${TOOLBAR_COLORS.BORDER}`}
            />

            {isSearching && (
                <MuiBox sx={{ padding: 2, textAlign: "center", color: "text.secondary" }}>{t("loadingResults")}</MuiBox>
            )}

            {searchError && (
                <MuiBox sx={{ padding: 2, textAlign: "center" }}>
                    <MuiBox sx={{ color: "error.main", fontWeight: 600 }}>{t("objectTreeSearchError")}</MuiBox>
                    <MuiBox sx={{ color: "text.secondary", fontSize: "0.875rem", mt: 1 }}>{searchError}</MuiBox>
                </MuiBox>
            )}

            {hasNoResults && !isSearching && (
                <MuiBox sx={{ padding: 2, textAlign: "center" }}>
                    <MuiBox sx={{ color: "text.primary", fontWeight: 600 }}>{t("noResultsFound")}</MuiBox>
                    <MuiBox sx={{ color: "text.secondary", fontSize: "0.875rem", mt: 1 }}>
                        {t("tryDifferentSearchTerm")}
                    </MuiBox>
                </MuiBox>
            )}

            {!isSearching && !searchError && !hasNoResults && (
                <TreeContainer>
                    <ModelTree
                        key={treeKey}
                        tree={tree}
                        setTree={setTree}
                        fileTypes={emptyFileTypeFilters}
                        NodeRenderer={ObjectTreeNodeProcore}
                        itemSize={ITEM_HEIGHT}
                        filteredObjectIds={filteredObjectIds}
                        searchTerm={searchTerm}
                        onNodeRightClick={onNodeRightClick}
                        isFilterActive={isFilterActive}
                    />
                </TreeContainer>
            )}

            <Menu
                open={contextMenu !== null}
                onClose={closeContextMenu}
                anchorReference="anchorPosition"
                anchorPosition={
                    contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined
                }
                slotProps={{
                    root: {
                        sx: { pointerEvents: "none" },
                    },
                }}
                PaperProps={{
                    ref: contextMenuPaperRef,
                    sx: {
                        pointerEvents: "auto",
                        WebkitTouchCallout: "none",
                        WebkitUserSelect: "none",
                        userSelect: "none",
                    },
                }}
                MenuListProps={{
                    sx: {
                        "& .MuiMenuItem-root, & .MuiListItemText-root, & .MuiTypography-root": {
                            WebkitTouchCallout: "none",
                            WebkitUserSelect: "none",
                            userSelect: "none",
                        },
                    },
                }}
            >
                {contextMenu?.actions.map((action) => (
                    <MenuItem key={action.id} onClick={action.onClick} disabled={action.disabled}>
                        <ListItemText>{action.label}</ListItemText>
                    </MenuItem>
                ))}
            </Menu>
        </PanelContent>
    );
}
